import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Box,
  CssBaseline,
  Drawer,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Header from "./Header";
import Sidebar from "./Sidebar";
import Footer from "./Footer";

// Drawer width for sidebar
const drawerWidth = 240;

/**
 * Main layout component that includes header, sidebar, content area, and footer
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render in the content area
 * @returns {JSX.Element} The Layout component
 */
const Layout = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user } = useSelector((state) => state.auth);
  const location = useLocation();

  // Check if current page is approval status page
  const isApprovalStatusPage = location.pathname === "/approval-status";

  // Determine if sidebar should be shown
  const showSidebar =
    user &&
    !(
      user.role === "buyer" &&
      (user.status === "pending" || user.status === "rejected")
    );

  /**
   * Handle drawer toggle for mobile view
   */
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <CssBaseline />

      {/* Header */}
      <Header
        drawerWidth={showSidebar ? drawerWidth : 0}
        handleDrawerToggle={handleDrawerToggle}
      />

      {/* Sidebar - only show if user is logged in and not in pending/rejected status */}
      {showSidebar && (
        <>
          {/* Mobile drawer */}
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true, // Better open performance on mobile
            }}
            sx={{
              display: { xs: "block", lg: "none" },
              "& .MuiDrawer-paper": {
                boxSizing: "border-box",
                width: drawerWidth,
              },
            }}
          >
            <Sidebar />
          </Drawer>

          {/* Desktop drawer */}
          <Drawer
            variant="permanent"
            sx={{
              display: { xs: "none", lg: "block" },
              "& .MuiDrawer-paper": {
                boxSizing: "border-box",
                width: drawerWidth,
              },
            }}
            open
          >
            <Sidebar />
          </Drawer>
        </>
      )}

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: {
            xs: "100%",
            lg: showSidebar ? `calc(100% - ${drawerWidth}px)` : "100%",
          },
          ml: { xs: 0, lg: showSidebar ? `${drawerWidth}px` : 0 },
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Toolbar spacer */}
        <Box sx={{ height: 64 }} />

        {/* Page content */}
        <Box sx={{ flex: 1 }}>{children}</Box>

        {/* Footer */}
        <Footer />
      </Box>
    </Box>
  );
};

export default Layout;
